'use client'

import {
  ThemeProvider,
  ToastProvider,
  StkUIProvider,
  DashboardShell,
  IdleTimeoutWatcher,
} from '@repo/ui'
import type { StkUIConfig, UserProfile } from '@repo/ui'
import {
  Globe,
  ShoppingCart,
  Users,
  UserCog,
  Package,
  Settings,
  BarChart3,
  CreditCard,
  Truck,
  ListChecks,
  SlidersHorizontal,
  ScrollText,
  LayoutDashboard,
  ClipboardList,
  Wallet,
  PackageSearch,
  UserRound,
  TruckIcon,
  FileText,
  FileSpreadsheet,
  Megaphone,
  MessageCircle,
  Warehouse,
  PackagePlus,
  AlertTriangle,
  PackageX,
  ShoppingBag,
  FilePenLine,
  PanelTop,
  PanelBottom,
  PackageCheck,
  TrendingUp,
  Gauge,
  CalendarDays,
  KeyRound,
  Coins,
  HandCoins,
} from 'lucide-react'
import { logout } from '@/app/actions/auth'
import dynamic from 'next/dynamic'
import { useEffect, useMemo, useState } from 'react'
import { AccountSheet } from '@/components/AccountSheet'
import { HeartbeatPinger } from '@/components/HeartbeatPinger'
import { BonusWidget } from '@/components/BonusWidget'

const GlobalSearch = dynamic(
  () => import('@/components/GlobalSearch').then((m) => m.GlobalSearch),
  { ssr: false }
)
import type { Module, UserPermissions } from '@/lib/permissions-shared'
import { canRead, canViewLogs, canViewOperatorReport } from '@/lib/permissions-shared'
import { getReadRoute } from '@/lib/read-route-client'
import { shouldPollWhatsappUnread } from '@/app/admin/leads/whatsapp-model'

// href → module eslestirmesi
const hrefModuleMap: Record<string, Module> = {
  '/admin': 'dashboard',
  '/admin/orders': 'orders',
  '/admin/sorunlu-kargolar': 'cargo_problems',
  '/admin/products': 'products',
  '/admin/sites': 'sites',
  '/admin/ana-site': 'sites',
  '/admin/sites/domainler': 'sites',
  '/admin/customers': 'customers',
  '/admin/reports': 'reports',
  '/admin/reports/siparis': 'reports',
  '/admin/reports/finansal': 'reports',
  '/admin/reports/urun': 'reports',
  '/admin/reports/musteri': 'reports',
  '/admin/reports/kargo': 'reports',
  '/admin/reports/site-satis': 'reports',
  '/admin/reports/gunluk-satis': 'reports',
  '/admin/reports/site-analiz': 'reports',
  '/admin/reports/site-teslimat': 'reports',
  '/admin/reports/maliyet': 'reports',
  '/admin/reports/manuel': 'reports',
  '/admin/users': 'users',
  '/admin/settings': 'settings',
  '/admin/settings/payment-methods': 'settings',
  '/admin/settings/cargos': 'settings',
  '/admin/settings/order-statuses': 'settings',
  '/admin/settings/order-sources': 'settings',
  '/admin/settings/general': 'settings',
  '/admin/settings/whatsapp': 'settings',
  '/admin/products/kategoriler': 'products',
  '/admin/settings/veri-aktarimi': 'settings',
  '/admin/settings/partner-api': 'settings',
  '/admin/kampanya-butceleri': 'settings',
  '/admin/kampanya-hesaplari': 'settings',
  '/admin/stock': 'stock',
  '/admin/stock/giris': 'stock',
  '/admin/stock/depolar': 'stock',
  '/admin/stock/kritik': 'stock',
  '/admin/stock/sorunlu-cikislar': 'stock',
  '/admin/whatsapp': 'whatsapp',
  '/admin/whatsapp/sablonlar': 'whatsapp_settings',
  '/admin/whatsapp/otomasyonlar': 'whatsapp_settings',
  '/admin/whatsapp/baglanti': 'whatsapp_settings',
}

type MenuItem = {
  label: string
  href: string
  badge?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any
  children?: MenuItem[]
}

const fullSidebarMenu: MenuItem[] = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { label: 'Siparişler', href: '/admin/orders', icon: ShoppingCart },
  { label: 'Sorunlu Kargolar', href: '/admin/sorunlu-kargolar', icon: PackageX },
  { label: 'Ürünler', href: '/admin/products', icon: Package },
  { label: 'Siteler', href: '/admin/sites', icon: Globe },
  { label: 'Müşteriler', href: '/admin/customers', icon: Users },
  {
    label: 'Raporlar',
    href: '/admin/reports',
    icon: BarChart3,
    children: [
      { label: 'Genel Özet', href: '/admin/reports', icon: BarChart3 },
      { label: 'Sipariş', href: '/admin/reports/siparis', icon: ClipboardList },
      { label: 'Finansal', href: '/admin/reports/finansal', icon: Wallet },
      { label: 'Ürün', href: '/admin/reports/urun', icon: PackageSearch },
      { label: 'Müşteri', href: '/admin/reports/musteri', icon: UserRound },
      { label: 'Kargo', href: '/admin/reports/kargo', icon: TruckIcon },
      { label: 'Site Satış', href: '/admin/reports/site-satis', icon: TrendingUp },
      { label: 'Günlük Satış', href: '/admin/reports/gunluk-satis', icon: CalendarDays },
      { label: 'Site Satış Analizi', href: '/admin/reports/site-analiz', icon: Gauge },
      { label: 'Site Teslimat', href: '/admin/reports/site-teslimat', icon: PackageCheck },
      { label: 'Maliyet Raporu', href: '/admin/reports/maliyet', icon: Coins },
      { label: 'Manuel Rapor', href: '/admin/reports/manuel', icon: FilePenLine },
    ],
  },
  {
    label: 'Stok',
    href: '/admin/stock',
    icon: Warehouse,
    children: [
      { label: 'Stok Listesi', href: '/admin/stock', icon: Package },
      { label: 'Stok Giriş/Çıkış', href: '/admin/stock/giris', icon: PackagePlus },
      { label: 'Stok Kalemleri', href: '/admin/stock/kalemler', icon: ClipboardList },
      { label: 'Depolar', href: '/admin/stock/depolar', icon: Warehouse },
      { label: 'Kritik Stoklar', href: '/admin/stock/kritik', icon: AlertTriangle },
      { label: 'Sorunlu Çıkışlar', href: '/admin/stock/sorunlu-cikislar', icon: PackageX },
    ],
  },
  { label: 'Reklam Hesapları', href: '/admin/kampanya-butceleri', icon: Megaphone },
  {
    label: 'WhatsApp',
    href: '/admin/whatsapp',
    icon: MessageCircle,
    children: [
      { label: 'Gelen Kutusu', href: '/admin/whatsapp', icon: MessageCircle },
      { label: 'Şablonlar', href: '/admin/whatsapp/sablonlar', icon: FileText },
      { label: 'Otomasyonlar', href: '/admin/whatsapp/otomasyonlar', icon: SlidersHorizontal },
      { label: 'Bağlantı', href: '/admin/whatsapp/baglanti', icon: Settings },
    ],
  },
  {
    label: 'E-Ticaret',
    href: '/admin/ecommerce/urunler',
    icon: ShoppingBag,
    children: [
      { label: 'Ürünler', href: '/admin/ecommerce/urunler', icon: Package },
      { label: 'Siparişler', href: '/admin/ecommerce/siparisler', icon: ListChecks },
      { label: 'Üyeler', href: '/admin/ecommerce/uyeler', icon: UserRound },
      { label: 'Kategoriler', href: '/admin/ecommerce/kategoriler', icon: ListChecks },
      { label: 'Markalar', href: '/admin/ecommerce/markalar', icon: PackageSearch },
      { label: 'Tedarikçi Feed\'leri', href: '/admin/ecommerce/feedler', icon: FileSpreadsheet },
      { label: 'Vitrin Düzenleyici', href: '/admin/ecommerce/vitrin', icon: SlidersHorizontal },
      { label: 'Header Düzenleyici', href: '/admin/ecommerce/header', icon: PanelTop },
      { label: 'Footer Düzenleyici', href: '/admin/ecommerce/footer', icon: PanelBottom },
      { label: 'Yasal Sayfalar', href: '/admin/ecommerce/sayfalar', icon: FileText },
      { label: 'Pazarlama & Analitik', href: '/admin/ecommerce/pazarlama', icon: Megaphone },
      { label: 'Ayarlar', href: '/admin/ecommerce/ayarlar', icon: Settings },
    ],
  },
  { label: 'Kullanıcılar', href: '/admin/users', icon: UserCog },
  { label: 'Operatör Raporu', href: '/admin/reports/operator', icon: Gauge },
  { label: 'Prim Hakediş', href: '/admin/prim-hakedis', icon: HandCoins },
  { label: 'Loglar', href: '/admin/loglar', icon: ScrollText },
  {
    label: 'Ayarlar',
    href: '/admin/settings',
    icon: Settings,
    children: [
{ label: 'Ödeme Yöntemleri', href: '/admin/settings/payment-methods', icon: CreditCard },
      { label: 'Kargo', href: '/admin/settings/cargos', icon: Truck },
      { label: 'Sipariş Durumları', href: '/admin/settings/order-statuses', icon: ListChecks },
      { label: 'Sipariş Kaynakları', href: '/admin/settings/order-sources', icon: Megaphone },
      { label: 'Genel Ayarlar', href: '/admin/settings/general', icon: SlidersHorizontal },
      { label: 'WhatsApp', href: '/admin/settings/whatsapp', icon: MessageCircle },
      { label: 'Veri Aktarımı', href: '/admin/settings/veri-aktarimi', icon: FileSpreadsheet },
      { label: 'Partner API', href: '/admin/settings/partner-api', icon: KeyRound },
      { label: 'Shopify', href: '/admin/settings/shopify', icon: ShoppingBag },
      { label: 'Prim Sistemi', href: '/admin/settings/prim', icon: HandCoins },
    ],
  },
]

/** Paket yükseltme primi menü bayrakları (layout'ta hesaplanır). */
export type UpgradeBonusMenuFlags = { enabled: boolean; isOperator: boolean; isManager: boolean }

const BONUS_PAGE_HREF = '/admin/prim-hakedis'
const BONUS_SETTINGS_HREF = '/admin/settings/prim'

function filterMenuByPermissions(
  menu: MenuItem[],
  perms: UserPermissions | null,
  bonus: UpgradeBonusMenuFlags | null,
): MenuItem[] {
  // Prim menüleri modül iznine değil prim bayraklarına bağlı: hakediş ekranı sistem açıkken
  // yöneticiye ve prim alan operatöre, ayarları yalnız owner/admin'e görünür.
  const showBonusPage = !!bonus && bonus.enabled && (bonus.isManager || bonus.isOperator)
  const showBonusSettings = !!bonus?.isManager
  menu = menu
    .filter((item) => item.href !== BONUS_PAGE_HREF || showBonusPage)
    .map((item) =>
      item.children?.some((c) => c.href === BONUS_SETTINGS_HREF) && !showBonusSettings
        ? { ...item, children: item.children.filter((c) => c.href !== BONUS_SETTINGS_HREF) }
        : item,
    )

  // Izin bilgisi yoksa tum menuyu goster (fallback)
  if (!perms) return menu

  return menu
    .map((item) => {
      if (item.href === BONUS_PAGE_HREF) return item

      // Prompt 87 — Loglar sayfası owner ve log_access'li admin'e gorunur.
      // Owner her seyi gormek icin bile bu filtreden gecmeli.
      if (item.href === '/admin/loglar') {
        return canViewLogs(perms) ? item : null
      }

      // Operatör Raporu owner ve operator_report_access'li admin'e görünür.
      // Owner dahil bu özel filtreden geçer (module map'e bağlı değil).
      if (item.href === '/admin/reports/operator') {
        return canViewOperatorReport(perms) ? item : null
      }

      // Owner geri kalan menu icin full erisim
      if (perms.isOwner) return item

      // Children varsa filtrele
      if (item.children) {
        const filteredChildren = item.children.filter((child) => {
          const mod = hrefModuleMap[child.href]
          if (!mod) return true
          return canRead(perms, mod)
        })
        if (filteredChildren.length === 0) return null
        return { ...item, children: filteredChildren }
      }

      // Tekil menu item
      const mod = hrefModuleMap[item.href]
      if (!mod) return item
      return canRead(perms, mod) ? item : null
    })
    .filter(Boolean) as MenuItem[]
}

const breadcrumbLabels: Record<string, string> = {
  admin: 'Dashboard',
  orders: 'Siparişler',
  'sorunlu-kargolar': 'Sorunlu Kargolar',
  products: 'Ürünler',
  sites: 'Siteler',
  customers: 'Müşteriler',
  reports: 'Raporlar',
  siparis: 'Sipariş',
  finansal: 'Finansal',
  urun: 'Ürün',
  musteri: 'Müşteri',
  kargo: 'Kargo',
  'site-satis': 'Site Satış',
  'gunluk-satis': 'Günlük Satış',
  'site-analiz': 'Site Satış Analizi',
  'site-teslimat': 'Site Teslimat',
  users: 'Kullanıcılar',
  operator: 'Operatör Raporu',
  'prim-hakedis': 'Prim Hakediş',
  prim: 'Prim Sistemi',
  loglar: 'Loglar',
  roles: 'Roller',
  settings: 'Ayarlar',
  'payment-methods': 'Ödeme Yöntemleri',
  cargos: 'Kargo',
  'order-statuses': 'Sipariş Durumları',
  general: 'Genel Ayarlar',
  whatsapp: 'WhatsApp',
  sablonlar: 'Şablonlar',
  otomasyonlar: 'Otomasyonlar',
  baglanti: 'Bağlantı',
  kategoriler: 'Kategoriler',
  kalemler: 'Stok Kalemleri',
  depolar: 'Depolar',
  domainler: 'Domainler',
  'kara-liste': 'Kara Liste',
  hesabim: 'Hesabım',
  stock: 'Stok',
  giris: 'Stok Giriş/Çıkış',
  kritik: 'Kritik Stoklar',
  'sorunlu-cikislar': 'Sorunlu Çıkışlar',
  'veri-aktarimi': 'Veri Aktarımı',
  'partner-api': 'Partner API',
  'kampanya-butceleri': 'Reklam Hesapları',
  'kampanya-hesaplari': 'Hesap Bağlantıları',
  'hesap-sec': 'Hesap Seç',
}

interface ProvidersProps {
  children: React.ReactNode
  profile: UserProfile | null
  permissions: UserPermissions | null
  stockModuleEnabled: boolean
  ecommerceModuleEnabled: boolean
  ecommerceSites?: { id: string; title: string }[]
  cargoApiEnabled: boolean
  upgradeBonus?: UpgradeBonusMenuFlags | null
  nonce?: string
}

export function Providers({ children, profile, permissions, stockModuleEnabled, ecommerceModuleEnabled, ecommerceSites = [], cargoApiEnabled, upgradeBonus = null, nonce }: ProvidersProps) {
  const [accountOpen, setAccountOpen] = useState(false)
  const [whatsappUnreadCount, setWhatsappUnreadCount] = useState(0)
  const canReadWhatsapp = Boolean(profile && permissions && canRead(permissions, 'whatsapp'))

  useEffect(() => {
    if (!canReadWhatsapp) return

    let disposed = false
    let intervalId: number | undefined
    let requestActive = false

    const refresh = async () => {
      if (requestActive || !shouldPollWhatsappUnread(true, document.visibilityState)) return
      requestActive = true
      try {
        const { count } = await getReadRoute<{ count: number }>('/api/whatsapp/unread-count')
        if (!disposed) setWhatsappUnreadCount(Math.max(0, count))
      } catch {
        // Menü, geçici bir sayaç hatasında kullanılabilir kalır.
      } finally {
        requestActive = false
      }
    }

    const syncPolling = () => {
      if (intervalId !== undefined) window.clearInterval(intervalId)
      intervalId = undefined
      if (!shouldPollWhatsappUnread(true, document.visibilityState)) return
      void refresh()
      intervalId = window.setInterval(() => void refresh(), 60_000)
    }

    syncPolling()
    document.addEventListener('visibilitychange', syncPolling)
    return () => {
      disposed = true
      if (intervalId !== undefined) window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', syncPolling)
    }
  }, [canReadWhatsapp])

  const dashboardConfig: StkUIConfig = useMemo(() => {
    let menu = filterMenuByPermissions(fullSidebarMenu, permissions, upgradeBonus)
    if (!stockModuleEnabled) {
      menu = menu.filter((item) => item.href !== '/admin/stock')
    }
    if (!ecommerceModuleEnabled) {
      menu = menu.filter((item) => item.label !== 'E-Ticaret')
    } else if (ecommerceSites.length >= 2) {
      // Aynı tenant'ta birden fazla e-ticaret mağazası → tek "E-Ticaret" yerine mağaza başına
      // ayrı başlık. Alt sayfalar ?site=<id> ile o mağazaya bağlanır (sayfalar bu param'ı okur).
      menu = menu.flatMap((item) => {
        if (item.label !== 'E-Ticaret') return [item]
        return ecommerceSites.map((site) => ({
          ...item,
          label: site.title,
          href: `/admin/ecommerce/urunler?site=${site.id}`,
          children: item.children?.map((c) => ({ ...c, href: `${c.href}?site=${site.id}` })),
        }))
      })
    }
    if (!cargoApiEnabled) {
      // Kargo API modülü kapalıysa Site Teslimat raporunu gizle.
      menu = menu.map((item) =>
        item.children
          ? { ...item, children: item.children.filter((c) => c.href !== '/admin/reports/site-teslimat') }
          : item
      )
    }

    if (whatsappUnreadCount > 0) {
      const badgeLabel = whatsappUnreadCount > 99 ? '99+' : String(whatsappUnreadCount)
      const WhatsappUnreadIcon = ({ className }: { className?: string }) => (
        <span className={`relative inline-flex ${className ?? ''}`}>
          <MessageCircle className="h-full w-full" aria-hidden="true" />
          <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[8px] font-bold leading-none text-destructive-foreground">
            {badgeLabel}
          </span>
        </span>
      )
      menu = menu.map((item) => {
        if (item.href !== '/admin/whatsapp') return item
        return {
          ...item,
          badge: whatsappUnreadCount,
          icon: WhatsappUnreadIcon,
          children: item.children?.map((child) =>
            child.href === '/admin/whatsapp'
              ? { ...child, badge: whatsappUnreadCount }
              : child,
          ),
        }
      })
    }
    const showBonusWidget = !!upgradeBonus?.enabled && !!upgradeBonus.isOperator
    return {
      sidebarMenu: menu,
      breadcrumbLabels,
      homePath: '/admin',
      sidebarFooterSlot: showBonusWidget ? ({ collapsed }: { collapsed: boolean }) => <BonusWidget collapsed={collapsed} /> : undefined,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, stockModuleEnabled, ecommerceModuleEnabled, cargoApiEnabled, whatsappUnreadCount, ecommerceSites.map((s) => `${s.id}:${s.title}`).join('|'), upgradeBonus?.enabled, upgradeBonus?.isOperator, upgradeBonus?.isManager])

  return (
    <ThemeProvider nonce={nonce}>
      {profile ? (
        <StkUIProvider config={dashboardConfig}>
          <DashboardShell
            profile={profile}
            onSignOut={() => logout()}
            onAccountClick={() => setAccountOpen(true)}
            headerSlot={<GlobalSearch />}
          >
            {children}
          </DashboardShell>
          <AccountSheet
            open={accountOpen}
            onClose={() => setAccountOpen(false)}
            email={profile.email ?? ''}
          />
          <IdleTimeoutWatcher
            idleMinutes={60}
            warnSeconds={60}
            onLogout={() => logout()}
            channelName="opop-admin-idle"
          />
          <HeartbeatPinger tenantId={permissions?.tenantId ?? null} />
        </StkUIProvider>
      ) : (
        children
      )}
      <ToastProvider />
    </ThemeProvider>
  )
}
